import { z } from 'zod'
import { emailSchema, publicIdSchema, utcTimestampSchema } from './primitives'

export const RESULT_VISIBILITY_VALUES = ['private', 'participants', 'public'] as const
export const resultVisibilitySchema = z.enum(RESULT_VISIBILITY_VALUES)
export type ResultVisibility = z.infer<typeof resultVisibilitySchema>

/** Public Competition Page v1 structured fields Spec Delta (2026-08-26). */
export const publicFaqEntrySchema = z.object({
  question: z.string().min(1).max(500),
  answer: z.string().min(1).max(5_000),
})
export type PublicFaqEntry = z.infer<typeof publicFaqEntrySchema>

export const competitionSettingsSchema = z.object({
  competitionId: publicIdSchema,
  contactEmail: emailSchema.nullable(),
  publicPageEnabled: z.boolean(),
  registrationInstructions: z.string().max(20_000).nullable(),
  resultVisibility: resultVisibilitySchema,
  enableTeacher: z.boolean(),
  enableVideoUrl: z.boolean(),
  enablePublicGallery: z.boolean(),
  enableMultiRound: z.boolean(),
  enableTeamInvites: z.boolean(),
  /** UX-138 (1): whether a judge's "審查意見" (org-visible review comment, distinct from their private note) must be non-empty before a score can be submitted for this competition. */
  reviewCommentRequired: z.boolean(),
  eligibilityText: z.string().max(20_000).nullable(),
  submissionRequirementsText: z.string().max(20_000).nullable(),
  judgingCriteriaText: z.string().max(20_000).nullable(),
  prizesText: z.string().max(20_000).nullable(),
  organizerContactText: z.string().max(20_000).nullable(),
  faq: z.array(publicFaqEntrySchema),
  createdAt: utcTimestampSchema,
  updatedAt: utcTimestampSchema,
})

export type CompetitionSettings = z.infer<typeof competitionSettingsSchema>

export const competitionSettingsDtoSchema = competitionSettingsSchema.omit({
  competitionId: true,
  createdAt: true,
})

export type CompetitionSettingsDto = z.infer<typeof competitionSettingsDtoSchema>

export const updateCompetitionSettingsRequestSchema = z
  .strictObject({
    contactEmail: emailSchema.nullable().optional(),
    publicPageEnabled: z.boolean().optional(),
    registrationInstructions: z.string().max(20_000).nullable().optional(),
    resultVisibility: resultVisibilitySchema.optional(),
    enableTeacher: z.boolean().optional(),
    enableVideoUrl: z.boolean().optional(),
    enablePublicGallery: z.boolean().optional(),
    enableMultiRound: z.boolean().optional(),
    enableTeamInvites: z.boolean().optional(),
    reviewCommentRequired: z.boolean().optional(),
    eligibilityText: z.string().max(20_000).nullable().optional(),
    submissionRequirementsText: z.string().max(20_000).nullable().optional(),
    judgingCriteriaText: z.string().max(20_000).nullable().optional(),
    prizesText: z.string().max(20_000).nullable().optional(),
    organizerContactText: z.string().max(20_000).nullable().optional(),
    faq: z.array(publicFaqEntrySchema).nullable().optional(),
  })
  .refine((value) => Object.keys(value).length > 0, {
    message: 'At least one field must be provided.',
  })

export type UpdateCompetitionSettingsRequest = z.infer<
  typeof updateCompetitionSettingsRequestSchema
>
